import { useState, useEffect } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Trash2, Briefcase, Bell, Database, Calculator, Receipt, FolderOpen, FileSignature, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { getNotificationsEnabled, setNotificationsEnabledExport } from "../MissingReportsAlert";
import { getBackupNotificationsEnabled, setBackupNotificationsEnabled } from "../BackupAlert";
const OptionsTab = ({
  employeeRoles,
  documentCategories = [],
  certifiedPayrollOfficial = null,
  defaultBurdenPercent,
  defaultSalesTaxPercent,
  billingOverdueDays = 30,
  addressHomeBase = "",
  userRole,
  getAuthHeaders,
  onRefresh,
}) => {
  const [newRole, setNewRole] = useState("");
  const [newDocCategory, setNewDocCategory] = useState("");
  const [official, setOfficial] = useState({ name: "", title: "", phone: "", email: "", ...(certifiedPayrollOfficial || {}) });
  useEffect(() => {
    if (certifiedPayrollOfficial) setOfficial({ name: "", title: "", phone: "", email: "", ...certifiedPayrollOfficial });
  }, [certifiedPayrollOfficial]);
  const saveOfficial = async () => {
    try {
      await apiClient.put(`/settings/options`, { certified_payroll_official: official });
      onRefresh();
      toast.success("Certifying official saved");
    } catch { toast.error("Failed to save certifying official"); }
  };
  const canEditBidDefaults = userRole === "admin" || userRole === "project_manager";

  // Bid defaults
  const [burdenPercent, setBurdenPercent] = useState(defaultBurdenPercent?.toString() || "28");
  const [salesTaxPercent, setSalesTaxPercent] = useState(defaultSalesTaxPercent?.toString() || "8.25");

  // Billing settings
  const [overdueDays, setOverdueDays] = useState(billingOverdueDays?.toString() || "30");

  // Address lookup home base
  const [homeBase, setHomeBase] = useState(addressHomeBase || "");
  useEffect(() => { setHomeBase(addressHomeBase || ""); }, [addressHomeBase]);
  const saveHomeBase = async () => {
    try {
      await apiClient.put(`/settings/options`, { address_home_base: homeBase.trim() });
      onRefresh();
      toast.success(homeBase.trim() ? "Home base saved — address lookups now favor this area" : "Home base cleared");
    } catch { toast.error("Failed to save home base"); }
  };

  // Notification settings
  const [missingReportsEnabled, setMissingReportsEnabled] = useState(() => getNotificationsEnabled());
  const [backupAlertsEnabled, setBackupAlertsEnabled] = useState(() => getBackupNotificationsEnabled());

  const handleMissingReportsToggle = (enabled) => {
    setNotificationsEnabledExport(enabled);
    setMissingReportsEnabled(enabled);
    toast.success(enabled ? "Missing reports notifications enabled" : "Missing reports notifications disabled");
  };

  const handleBackupAlertsToggle = (enabled) => {
    setBackupNotificationsEnabled(enabled);
    setBackupAlertsEnabled(enabled);
    toast.success(enabled ? "Backup reminder notifications enabled" : "Backup reminder notifications disabled");
  };

  const addRole = async () => {
    if (!newRole.trim()) return;
    if (employeeRoles.includes(newRole.trim())) { toast.error("Role already exists"); return; }
    try {
      await apiClient.put(`/settings/options`, { employee_roles: [...employeeRoles, newRole.trim()] });
      onRefresh();
      setNewRole("");
      toast.success("Role added");
    } catch { toast.error("Failed to add role"); }
  };

  const removeRole = async (role) => {
    try {
      await apiClient.put(`/settings/options`, { employee_roles: employeeRoles.filter(r => r !== role) });
      onRefresh();
      toast.success("Role removed");
    } catch { toast.error("Failed to remove role"); }
  };

  const addDocCategory = async () => {
    const name = newDocCategory.trim();
    if (!name) return;
    if (documentCategories.some((c) => c.toLowerCase() === name.toLowerCase())) { toast.error("Category already exists"); return; }
    try {
      await apiClient.put(`/settings/options`, { document_categories: [...documentCategories, name] });
      setNewDocCategory("");
      onRefresh();
      toast.success("Document category added");
    } catch { toast.error("Failed to add category"); }
  };

  const removeDocCategory = async (name) => {
    try {
      await apiClient.put(`/settings/options`, { document_categories: documentCategories.filter((c) => c !== name) });
      onRefresh();
      toast.success("Document category removed");
    } catch { toast.error("Failed to remove category"); }
  };

  const saveBidDefaults = async () => {
    try {
      await apiClient.put(`/settings/options`, {
        default_burden_percent: parseFloat(burdenPercent) || 28,
        default_sales_tax_percent: parseFloat(salesTaxPercent) || 8.25,
      });
      onRefresh();
      toast.success("Bid defaults saved");
    } catch { toast.error("Failed to save bid defaults"); }
  };

  const saveBillingSettings = async () => {
    const days = parseInt(overdueDays) || 30;
    if (days < 1 || days > 365) { toast.error("Overdue days must be between 1 and 365"); return; }
    try {
      await apiClient.put(`/settings/options`, { billing_overdue_days: days });
      onRefresh();
      toast.success("Billing settings saved");
    } catch { toast.error("Failed to save billing settings"); }
  };

  return (
    <div className="space-y-6" data-testid="options-tab">
      {/* Bid Defaults */}
      {canEditBidDefaults && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Bid Defaults
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="burden-percent">Default Burden % (Labor Only)</Label>
                <div className="flex gap-2">
                  <NumericInput id="burden-percent" decimals={2} min={0} value={burdenPercent} onChange={(e) => setBurdenPercent(e.target.value)} placeholder="28" className="w-32" data-testid="burden-percent-input" />
                  <span className="flex items-center text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">Applied to labor costs for burden/overhead</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sales-tax-percent">Default Sales Tax % (Materials Only)</Label>
                <div className="flex gap-2">
                  <NumericInput id="sales-tax-percent" decimals={2} min={0} value={salesTaxPercent} onChange={(e) => setSalesTaxPercent(e.target.value)} placeholder="8.25" className="w-32" data-testid="sales-tax-percent-input" />
                  <span className="flex items-center text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">Applied to materials (unless tax exempt)</p>
              </div>
              <div className="flex items-end">
                <Button onClick={saveBidDefaults} data-testid="save-bid-defaults-btn">Save Defaults</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Settings */}
      {canEditBidDefaults && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Billing Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="overdue-days">Auto-Overdue After (Days)</Label>
                <div className="flex gap-2">
                  <NumericInput id="overdue-days" decimals={0} min={1} max={365} value={overdueDays} onChange={(e) => setOverdueDays(e.target.value)} placeholder="30" className="w-32" data-testid="overdue-days-input" />
                  <span className="flex items-center text-muted-foreground">days</span>
                </div>
                <p className="text-xs text-muted-foreground">Billings not paid within this period will be automatically marked as overdue</p>
              </div>
              <div className="flex items-end">
                <Button onClick={saveBillingSettings} data-testid="save-billing-settings-btn">Save Settings</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Address lookup home base */}
      {canEditBidDefaults && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Address Lookup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="home-base">Home Base (City, State or ZIP)</Label>
                <Input id="home-base" value={homeBase} onChange={(e) => setHomeBase(e.target.value)} placeholder="e.g. Temple, TX" data-testid="home-base-input" />
                <p className="text-xs text-muted-foreground">Address suggestions are ranked by distance from here, so partial addresses match your service area first</p>
              </div>
              <div className="flex items-end">
                <Button onClick={saveHomeBase} data-testid="save-home-base-btn">Save Home Base</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employee Roles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Employee Roles
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="Add new role (e.g., Foreman)" onKeyDown={(e) => e.key === "Enter" && addRole()} data-testid="new-role-input" />
            <Button onClick={addRole} data-testid="add-role-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {employeeRoles.map((role) => (
              <div key={role} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                <span>{role}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeRole(role)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {employeeRoles.length === 0 && <p className="text-muted-foreground text-center py-4">No roles defined</p>}
          </div>
        </CardContent>
      </Card>

      {/* Certified Payroll — certifying official */}
      <Card data-testid="certified-payroll-official-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileSignature className="w-4 h-4" />
            Certified Payroll — Certifying Official
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">Printed on page 2 of every WH-347 (name/title, phone, email). The signature line is left blank for a wet or PDF signature.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input value={official.name} onChange={(e) => setOfficial({ ...official, name: e.target.value })} placeholder="Full name" data-testid="cp-official-name" />
            <Input value={official.title} onChange={(e) => setOfficial({ ...official, title: e.target.value })} placeholder="Title (e.g., Office Manager)" data-testid="cp-official-title" />
            <Input value={official.phone} onChange={(e) => setOfficial({ ...official, phone: e.target.value })} placeholder="(254) 555-0100" data-testid="cp-official-phone" />
            <Input value={official.email} onChange={(e) => setOfficial({ ...official, email: e.target.value })} placeholder="ap@v2rmasonry.com" data-testid="cp-official-email" />
          </div>
          <Button onClick={saveOfficial} data-testid="cp-official-save">Save</Button>
        </CardContent>
      </Card>

      {/* Document Categories */}
      <Card data-testid="document-categories-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Document Categories
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Folders used on each job's Documents tab (also the sub-folder names in OneDrive under "V2R Masonry Documents / Job").</p>
          <div className="flex gap-2">
            <Input value={newDocCategory} onChange={(e) => setNewDocCategory(e.target.value)} placeholder="Add category (e.g., RFIs)" onKeyDown={(e) => e.key === "Enter" && addDocCategory()} data-testid="new-doc-category-input" />
            <Button onClick={addDocCategory} data-testid="add-doc-category-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {documentCategories.map((c) => (
              <div key={c} className="flex items-center justify-between p-2 bg-muted/30 rounded" data-testid={`doc-category-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                <span>{c}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeDocCategory(c)} data-testid={`remove-doc-category-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {documentCategories.length === 0 && <p className="text-muted-foreground text-center py-4">No categories defined</p>}
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notification Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className={`w-5 h-5 ${missingReportsEnabled ? "text-green-600" : "text-gray-400"}`} />
              <div>
                <Label htmlFor="missing-reports-toggle" className="font-medium cursor-pointer">Missing Daily Reports Alert</Label>
                <p className="text-sm text-muted-foreground">Show reminders for active jobs missing daily reports</p>
              </div>
            </div>
            <Switch id="missing-reports-toggle" checked={missingReportsEnabled} onCheckedChange={handleMissingReportsToggle} data-testid="missing-reports-toggle" />
          </div>
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Database className={`w-5 h-5 ${backupAlertsEnabled ? "text-green-600" : "text-gray-400"}`} />
              <div>
                <Label htmlFor="backup-alerts-toggle" className="font-medium cursor-pointer">Backup Reminder Alert</Label>
                <p className="text-sm text-muted-foreground">Show reminder if no backup completed in the last 5 days</p>
              </div>
            </div>
            <Switch id="backup-alerts-toggle" checked={backupAlertsEnabled} onCheckedChange={handleBackupAlertsToggle} data-testid="backup-alerts-toggle" />
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default OptionsTab;
